import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  FiChevronLeft, FiChevronRight, FiDownload, FiUploadCloud, FiFileText,
  FiLock, FiUnlock, FiSave, FiChevronDown, FiChevronRight as FiChevronRt, FiAlertCircle,
} from 'react-icons/fi';

import salaryService from '../../services/api/salaryService';
import PayrollNumberInput from '../../components/admin/PayrollNumberInput';
import { matchesPayrollSearch } from '../../utils/payrollSearch';
import { payrollHelp } from '../../utils/payrollHelp';
import { isPaid, isSalaryAdvance, hasPayableInputs, hasPayoutInputs, payrollReason, forwardDestination } from '../../utils/payrollDisplay';
import {
  formatPlacementDate,
  getPlacements,
  placementCustomerName,
  salaryItemAmount,
  salaryItemsForRow,
  unallocatedAmountMinor,
} from '../../utils/adminV2';

const Page = styled.div`width: 100%; max-width: none; min-width: 0; height: var(--payroll-height, calc(100dvh - 130px)); display: flex; flex-direction: column; gap: .35rem; overflow-anchor: none; > * { flex-shrink: 0; }`;
const Card = styled.div`background: #fff; border-radius: 0.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.08); padding: 1.1rem 1.35rem; margin-bottom: 1.1rem;`;
const Toolbar = styled(Card)`margin: 0; padding: .65rem 1rem; @media(max-width: 600px) { padding: .45rem; label { font-size: 0; gap: 0; } label select { font-size: .8rem; max-width: 160px; } button { font-size: .78rem; padding: .35rem .45rem; } }`;
const PayrollCard = styled(Card)`margin: 0; padding: .5rem 1rem; flex: 1 1 0; min-height: 0; display: flex; flex-direction: column;`;
const Footer = styled.div`flex: 0 0 auto; min-width: 0; background: #fff; border-top: 1px solid var(--border-subtle); padding-top: .3rem; @media(max-width: 1100px), (max-height: 750px) { > div:last-child { flex-wrap: nowrap; overflow-x: auto; padding-bottom: .35rem; > span { flex-shrink: 0; } } }`;
const Details = styled.details`background: #fff; border-radius: .4rem; padding: .45rem .75rem; font-size: .82rem; > summary { cursor: pointer; font-weight: 600; } > div { max-height: 22vh; overflow: auto; }`;
const DetailsBar = styled.div`display: flex; gap: .5rem; position: relative; > details { flex: 1; min-width: 0; } > details[open] > div { position: absolute; top: 100%; left: 0; right: 0; z-index: 10; max-height: 45vh; padding: 1rem; background: #fff; border: 1px solid var(--border-strong); box-shadow: 0 3px 10px #0002; }`;
const StatusSlot = styled.div`height: 1.9rem; overflow: auto; flex-shrink: 0; > div { padding: .25rem .6rem; margin: 0; font-size: .8rem; }`;
const SearchInput = styled.input`min-width: 180px; flex: 1; padding: .4rem .55rem; border: 1px solid var(--border-strong); border-radius: .25rem;`;
const Info = styled.span.attrs({ tabIndex: 0, role: 'img', 'aria-label': 'Information' })`display: inline-flex; justify-content: center; align-items: center; width: 1rem; height: 1rem; border: 1px solid currentColor; border-radius: 50%; font-size: .65rem; font-weight: 700; margin-left: .35rem; vertical-align: middle; cursor: help; flex-shrink: 0;`;
const Help = ({ text }) => <Info title={text} aria-label={`Information: ${text}`}>i</Info>;
const ActionHelp = styled.span`display: inline-flex; align-items: center; color: var(--ve-ink-600);`;
const Bar = styled.div`display: flex; align-items: center; gap: .6rem; flex-wrap: wrap; justify-content: space-between;`;
const Group = styled.div`display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap;`;
const MonthNav = styled.div`display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;`;
const NavBtn = styled.button`display: inline-flex; align-items: center; padding: 0.45rem 0.7rem; border: 1px solid var(--border-strong); border-radius: 0.25rem; background: #fff; color: var(--ve-ink-800); font-weight: 600; cursor: pointer; & svg { color: var(--ve-ink-800); } &:hover { background: var(--ve-blue-050); border-color: var(--act-primary); color: var(--act-primary); }`;
const MonthLabel = styled.span`font-weight: 600; color: var(--ve-ink-800); min-width: 120px; text-align: center; text-transform: capitalize;`;
const FilterLabel = styled.label`display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.85rem; color: var(--ve-ink-600); font-weight: 600;`;
const Sel = styled.select`width: auto; max-width: 100%; padding: 0.35rem 0.5rem; border: 1px solid var(--border-subtle); border-radius: 0.25rem; font-size: 0.85rem; color: var(--ve-ink-800); background: #fff; &:focus { outline: none; border-color: var(--act-primary); }`;
const Toggle = styled.button`display: inline-flex; align-items: center; gap: 0.4rem; background: none; border: 1px solid var(--border-subtle); border-radius: 0.25rem; color: var(--ve-ink-600); font-size: 0.85rem; font-weight: 600; padding: 0.4rem 0.7rem; cursor: pointer; &:hover { border-color: var(--act-primary); color: var(--act-primary); }`;
const LockPill = styled.span`display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.8rem; font-weight: 600; padding: 0.2rem 0.6rem; border-radius: 999px; color: ${({ $locked }) => ($locked ? 'var(--act-danger)' : '#1e8449')}; background: ${({ $locked }) => ($locked ? '#fdedec' : '#eafaf1')}; @media(max-width: 600px) { display: none; }`;
const FxBar = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; flex-wrap: wrap; margin-top: 0.4rem; padding-top: 0.4rem; border-top: 1px solid var(--surface-subtle);`;
const FxSummary = styled.div`display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; color: var(--ve-ink-800); font-size: 0.86rem;`;
const FxStatus = styled.span`display: inline-flex; align-items: center; padding: 0.15rem 0.55rem; border-radius: 999px; font-size: 0.75rem; font-weight: 700; color: ${({ $frozen }) => ($frozen ? '#1e8449' : '#9a6a10')}; background: ${({ $frozen }) => ($frozen ? '#eafaf1' : '#fdf4e6')};`;
const FxMeta = styled.span`color: var(--ve-ink-500); font-size: 0.78rem; @media(max-width: 900px), (max-height: 800px) { display: none; }`;

/* The header only sticks against a scroll container that actually has a
   height, so the wrapper is capped instead of letting the page grow: a bare
   `overflow-x: auto` computes overflow-y to auto as well, but with no height it
   never scrolls and `position: sticky` has nothing to stick to. Capping it here
   also keeps the action buttons and the month total on screen. */
const TableWrap = styled.div`
  overflow: auto;
  flex: 1 1 0;
  min-height: 0;
  overflow-anchor: none;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
`;
// Keep every column reachable at smaller viewport widths.
const Table = styled.table`width: 100%; border-collapse: separate; border-spacing: 0; table-layout: fixed; min-width: ${({ $wide }) => ($wide ? '1060px' : '2240px')};`;
const Th = styled.th`
  text-align: ${({ $num }) => ($num ? 'right' : 'left')};
  padding: 0.5rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--ve-ink-600);
  white-space: normal;
  position: sticky;
  top: 0;
  z-index: 2;
  /* An opaque background is load-bearing: the default transparent th would let
     the rows scroll through underneath it. The border moves to box-shadow for
     the same reason — a border-bottom on a sticky cell scrolls away with the
     collapsed table border. */
  background: #fff;
  box-shadow: inset 0 -1px 0 var(--border-subtle);
`;
const Td = styled.td`padding: 0.45rem 0.6rem; font-size: 0.85rem; color: var(--ve-ink-800); border-bottom: 1px solid var(--surface-subtle); text-align: ${({ $num }) => ($num ? 'right' : 'left')}; overflow-wrap: anywhere; white-space: normal; font-variant-numeric: ${({ $num }) => ($num ? 'tabular-nums' : 'normal')};`;
const NumInput = styled.input`width: 96px; text-align: right; padding: 0.3rem 0.4rem; border: 1px solid var(--border-subtle); border-radius: 0.25rem; font-size: 0.83rem; &:focus { outline: none; border-color: var(--act-primary); } &:disabled { background: var(--surface-subtle); color: var(--ve-ink-500); }`;
const AmountInput = styled(PayrollNumberInput)`width: 110px; box-sizing: border-box; text-align: right; padding: .3rem .4rem; border: 1px solid var(--border-subtle); border-radius: .25rem; font-size: .83rem; &:focus { outline: none; border-color: var(--act-primary); } &:disabled { background: var(--surface-subtle); color: var(--ve-ink-500); }`;
const NoteInput = styled.input`width: 160px; padding: 0.3rem 0.4rem; border: 1px solid var(--border-subtle); border-radius: 0.25rem; font-size: 0.83rem; &:focus { outline: none; border-color: var(--act-primary); } &:disabled { background: var(--surface-subtle); color: var(--ve-ink-500); }`;
// Overfør button: gray by default, red once the month has been carried over (REQ #23).
const XferBtn = styled.button`padding: 0.32rem 0.7rem; border-radius: 0.25rem; font-size: 0.78rem; font-weight: 600; cursor: pointer; color: #fff; border: 1px solid ${({ $done }) => ($done ? 'var(--act-danger)' : '#98a2ad')}; background: ${({ $done }) => ($done ? 'var(--act-danger)' : 'var(--ve-ink-500)')}; &:hover { filter: brightness(0.94); } &:disabled { cursor: not-allowed; opacity: ${({ $done }) => ($done ? 1 : 0.45)}; }`;
const PayrollRow = styled.tr`background: ${({ $paid }) => $paid ? '#f1f3f5' : '#fff'}; ${({ $paid }) => $paid && 'td, td strong, td span { color: #667085; }'} ${({ $expanded }) => $expanded && 'td { border-top: 2px solid var(--act-primary); background: #f4f9fb; } td:first-child { border-left: 2px solid var(--act-primary); } td:last-child { border-right: 2px solid var(--act-primary); }'}`;
const SpecialRow = styled.tr`> td { border: 2px solid var(--act-primary); border-top: 0; border-bottom-width: 3px; }`;
const Reason = styled.div`font-size: 0.76rem; line-height: 1.35; margin-top: 0.3rem; white-space: normal; max-width: 260px;`;
const Badge = styled.span`display: inline-block; font-size: 0.72rem; font-weight: 600; padding: 0.1rem 0.5rem; border-radius: 999px; color: ${({ $on }) => ($on === true ? '#1e8449' : $on === false ? 'var(--act-danger)' : 'var(--ve-ink-500)')}; background: ${({ $on }) => ($on === true ? '#eafaf1' : $on === false ? '#fdedec' : 'var(--surface-subtle)')};`;
const UnallocatedPill = styled.span`display: inline-flex; margin-left: 0.35rem; padding: 0.1rem 0.5rem; border-radius: 999px; color: #9a6a10; background: #fdf4e6; font-size: 0.7rem; font-weight: 700;`;
const ItemPanel = styled.div`padding: 0.65rem 0.75rem; background: var(--surface-subtle); border-left: 3px solid var(--act-primary); white-space: normal;`;
const ItemTitle = styled.div`font-size: 0.78rem; font-weight: 700; color: var(--ve-ink-700); margin-bottom: 0.45rem;`;
const ItemRow = styled.div`display: grid; grid-template-columns: minmax(110px, 0.7fr) minmax(100px, 0.55fr) minmax(120px, 0.6fr) minmax(260px, 1.7fr); align-items: center; gap: 0.6rem; padding: 0.35rem 0; border-top: 1px solid var(--border-subtle); font-size: 0.8rem; @media (max-width: 850px) { grid-template-columns: 1fr; }`;
/* The four editable special-salary fields, stacked as a wrapping grid of
   labelled cells inside the row's panel. This is what replaces the four extra
   table columns: it wraps instead of extending the table sideways, so nothing
   is ever cut off and there is no horizontal scrolling to fight. */
const ItemFields = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 0.55rem 0.9rem;
  padding-bottom: 0.5rem;
`;
const Field = styled.label`display: flex; flex-direction: column; gap: 0.2rem; font-size: 0.75rem; font-weight: 600; color: var(--ve-ink-600);`;
const FieldRow = styled.div`display: flex; align-items: center; gap: 0.35rem;`;
const Unit = styled.span`font-size: 0.72rem; font-weight: 600; color: var(--ve-ink-500); white-space: nowrap;`;
const Derived = styled.div`font-size: 0.72rem; font-weight: 600; color: var(--act-primary); min-height: 1em;`;
const CarriedIn = styled.div`font-size: 0.7rem; color: var(--act-primary);`;
const AllocationSelect = styled.select`width: 100%; padding: 0.35rem 0.45rem; border: 1px solid ${({ $unallocated }) => ($unallocated ? '#eea82f' : 'var(--border-subtle)')}; border-radius: 0.25rem; background: #fff; color: var(--ve-ink-800); &:focus { outline: none; border-color: var(--act-primary); } &:disabled { background: var(--surface-subtle); }`;

const Actions = styled.div`display: flex; gap: 0.6rem; margin-top: 1rem; flex-wrap: wrap; align-items: center;`;
const Btn = styled.button`display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.5rem 1rem; border-radius: 0.25rem; font-size: 0.88rem; font-weight: 600; cursor: pointer; border: none; color: #fff; background: ${({ $variant }) => ($variant === 'ghost' ? 'var(--ve-ink-600)' : $variant === 'download' ? 'var(--act-primary)' : $variant === 'danger' ? 'var(--act-danger)' : 'var(--act-primary)')}; &:hover { filter: brightness(0.94); } &:disabled { opacity: 0.5; cursor: not-allowed; }`;
const Alert = styled.div`padding: 0.75rem 1rem; border-radius: 0.25rem; margin-bottom: 1rem; font-size: 0.9rem; background: ${({ $type }) => ($type === 'error' ? 'var(--status-danger-plate)' : '#eafaf1')}; color: ${({ $type }) => ($type === 'error' ? 'var(--act-danger)' : '#1e8449')}; border: 1px solid ${({ $type }) => ($type === 'error' ? '#f5c6cb' : '#abebc6')};`;
const Empty = styled.div`flex: 1 1 0; min-height: 0; overflow: auto; padding: 1.5rem; text-align: center; color: var(--ve-ink-500);`;
const Total = styled.div`display: flex; justify-content: space-between; flex-wrap: wrap; gap: .25rem; padding: .3rem .4rem 0; font-weight: 700; color: var(--ve-ink-800);`;

/* Simple confirm modal */
const Overlay = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000;`;
const Dialog = styled.div`background: #fff; border-radius: 0.5rem; padding: 1.5rem; max-width: 440px; width: 92%; max-height: 90vh; overflow-y: auto;`;
const DlgTitle = styled.h3`margin: 0 0 0.6rem; color: var(--ve-ink-800);`;
const DlgText = styled.p`color: var(--ve-ink-600); font-size: 0.9rem; margin: 0 0 1.2rem;`;
const AllocationList = styled.ul`padding-left: 1.2rem; margin: 0 0 1.2rem; color: var(--ve-ink-800); font-size: .85rem; li { margin-bottom: .8rem; overflow-wrap: anywhere; } span { display: block; margin-top: .2rem; }`;
const Ack = styled.label`display: flex; align-items: flex-start; gap: 0.55rem; margin: 0 0 1.2rem; padding: 0.7rem; background: var(--status-warning-plate, #fdf4e6); color: var(--ve-ink-700); font-size: 0.85rem; cursor: pointer; input { margin-top: 0.15rem; }`;

const fmt = (n) => (Number(n) || 0).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Whole-DKK amount with da-DK thousand separators, e.g. "12.345 kr" (Bruttoløn, REQ #21).
const fmtDkk = (n) => `${(Number(n) || 0).toLocaleString('da-DK', { maximumFractionDigits: 0 })} kr`;
const fmtItemAmount = (item) => `${salaryItemAmount(item).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${item.currency || 'DKK'}`;
// 'travel' is the stored salary_items type; the operator-facing name for it is
// now "Refund." — the type is not renamed because it is a persisted DB enum.
const salaryTypeLabel = (type) => ({ premium: 'Premium', advance: 'Advance', travel: 'Refund.' }[type] || type || 'Salary item');
const fmtTimestamp = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
};
const settingsLinkFromError = (error) => {
  return error.details?.configurationUrl === '/admin/integrations#danlon-payroll-settings'
    || /^(DANLON_|PAYROLL_SETTINGS_|PAYROLL_MAPPING_)/.test(error.code || '')
    ? '/admin/integrations#danlon-payroll-settings' : null;
};
const transferRequestId = () => {
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const TaxCardSummary = ({ row, t, unsaved = false }) => {
  const plan = row.taxCardPayroll;
  if (!plan) return null;
  const settlementGross = !isSalaryAdvance(row) && Number(plan.advanceRepaymentDkkMinor) > 0
    && Array.isArray(plan.sourcePeriods) && plan.sourcePeriods.length > 0
    && plan.sourcePeriods.every((source) => Number.isSafeInteger(source.grossDkkMinor))
    ? plan.sourcePeriods.reduce((total, source) => total + source.grossDkkMinor, 0) : null;
  return <div style={{ minHeight: 200 }}>
    <strong>{isSalaryAdvance(row)
      ? t('admin.payroll.salaryAdvance', { defaultValue: 'Forskud (Bérelöleg)' })
      : t('admin.payroll.regularSalary', { defaultValue: 'Løn (Bér)' })}</strong>
    <div style={{ minHeight: 22, fontSize: '.72rem' }}>{unsaved && t('admin.payroll.estimateUnsaved', { defaultValue: 'Save changes to refresh the calculation.' })}</div>
    {plan.error ? <Reason style={{ color: 'var(--act-danger)' }}>{plan.error.message || String(plan.error)}</Reason>
        : <>
          {settlementGross != null && <Reason>
            {t('admin.payroll.grossToSettle', { defaultValue: 'Gross earnings to settle' })}: <strong>{fmt(settlementGross / 100)} DKK</strong>
          </Reason>}
          {isSalaryAdvance(row) && plan.payNowDkkMinor != null && <Reason>
            {isPaid(row) ? t('admin.payroll.recordedAdvance', { defaultValue: 'Recorded advance' })
              : t('admin.payroll.estimatedAdvance', { defaultValue: 'Estimated advance' })}: <strong>{fmt(plan.payNowDkkMinor / 100)} DKK</strong>
          </Reason>}
          {Number(plan.advanceRepaymentDkkMinor) !== 0 && plan.advanceRepaymentDkkMinor != null && <Reason>
            {t('admin.payroll.advanceRepayment', { defaultValue: 'Forskud repayment' })}: {fmt(plan.advanceRepaymentDkkMinor / 100)} DKK
          </Reason>}
          {Number(plan.advanceBalanceDkkMinor) !== 0 && plan.advanceBalanceDkkMinor != null && <Reason>
            {isPaid(row) ? t('admin.payroll.remainingAdvance', { defaultValue: 'Remaining Forskud balance' })
              : t('admin.payroll.advanceAfterPayment', { defaultValue: 'Forskud balance after this payment' })}: {fmt(plan.advanceBalanceDkkMinor / 100)} DKK
          </Reason>}
          {Number(plan.pendingDeductionDkkMinor) !== 0 && plan.pendingDeductionDkkMinor != null && <Reason>
            {t('admin.payroll.deferredAdjustments', { defaultValue: 'Deferred Forudbetaling / Refund' })}: {fmt(plan.pendingDeductionDkkMinor / 100)} DKK
          </Reason>}
          {isSalaryAdvance(row) && plan.payNowDkkMinor != null && plan.advanceBalanceDkkMinor != null && <Reason data-testid="advance-equation">
            Previous advances {fmt((plan.advanceBalanceDkkMinor - plan.payNowDkkMinor) / 100)} + this advance {fmt(plan.payNowDkkMinor / 100)} = <strong>{fmt(plan.advanceBalanceDkkMinor / 100)} DKK</strong>
            <Help text="The balance already includes this advance. Deferred Forudbetaling / Refund is settled later and is not part of this payment total." />
          </Reason>}
        </>}
  </div>;
};

const Payroll = () => {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const tempusFilter = 'all';
  const danlonFilter = 'all';
  const [paymentView, setPaymentView] = useState('unpaid');
  const [showSpecial, setShowSpecial] = useState(false);
  const [search, setSearch] = useState('');
  const [taxFilter, setTaxFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [doneByMonth, setDoneByMonth] = useState({});
  const doneKey = `${year}-${month}`;
  const done = doneByMonth[doneKey] || new Set();
  const toggleDone = (employeeId) => setDoneByMonth((prev) => {
    const next = new Set(prev[doneKey] || []);
    if (next.has(employeeId)) next.delete(employeeId); else next.add(employeeId);
    return { ...prev, [doneKey]: next };
  });
  const pageRef = useRef(null);
  // Size the work area from its actual position, including the responsive app
  // header. Only the table scrolls; toolbar and footer stay in view together.
  useLayoutEffect(() => {
    const resize = () => {
      if (!pageRef.current) return;
      const top = pageRef.current.getBoundingClientRect().top + window.scrollY;
      pageRef.current.style.setProperty('--payroll-height', `${Math.max(320, window.innerHeight - top - 12)}px`);
    };
    resize();
    window.addEventListener('resize', resize);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
    const header = document.querySelector('.admin-header');
    if (header) observer?.observe(header);
    return () => { window.removeEventListener('resize', resize); observer?.disconnect(); };
  }, []);

  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  // Select unpaid payable hours and special items, including carried amounts.
  const [selected, setSelected] = useState(() => new Set());
  // Selection is a shared per-employee/month preference, independent of the
  // financial inputs. Explicit false must survive saving, export and reopening.
  const selectionDirty = useRef(new Map());
  const dirty = useRef(new Set());
  // Rows whose Admin-Timer field the admin actually edited — tracked separately
  // so we only ever send adminHours when it was really touched (not merely when
  // an unrelated field on the row changed).
  const adminDirty = useRef(new Set());
  const allocationDirty = useRef(new Set());
  const [loading, setLoading] = useState(true);
  const [working, setBusy] = useState(false);
  const [refreshRequired, setRefreshRequired] = useState(false);
  const busy = working || loading || refreshRequired;
  const [error, setError] = useState(null);
  const [errorConfigurationUrl, setErrorConfigurationUrl] = useState(null);
  const [notice, setNotice] = useState(null);
  const [danlonTransfers, setDanlonTransfers] = useState([]);
  const [transferHistoryError, setTransferHistoryError] = useState('');
  const [resolveTransfer, setResolveTransfer] = useState(null);
  const [resolveReason, setResolveReason] = useState('');
  const [resolveAcknowledged, setResolveAcknowledged] = useState(false);
  const transferAttempt = useRef(null);
  const [confirm, setConfirm] = useState(null); // { title, text, onYes }
  const [confirmAcknowledged, setConfirmAcknowledged] = useState(false);
  const [paymentRow, setPaymentRow] = useState(null);
  const [paidDate, setPaidDate] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const importRef = useRef(null);
  // Contested days awaiting an admin decision (REQ #13)
  const [disputes, setDisputes] = useState([]);
  const [settleInputs, setSettleInputs] = useState({}); // timeEntryId -> hours string

  const loadDisputes = useCallback(async () => {
    try {
      const res = await salaryService.getDisputes(false);
      const list = (res && res.disputes) || [];
      setDisputes(list);
      // Pre-fill each settle box with the customer-suggested hours (fallback: logged).
      setSettleInputs((prev) => {
        const next = { ...prev };
        list.forEach((d) => {
          if (next[d.timeEntryId] === undefined) {
            next[d.timeEntryId] = String(d.suggestedHours != null ? d.suggestedHours : d.loggedHours);
          }
        });
        return next;
      });
    } catch (err) { /* disputes are secondary; keep the payroll page usable */ }
  }, []);

  const settleDispute = (timeEntryId) => run(async () => {
    const hours = Number(settleInputs[timeEntryId]);
    if (!Number.isFinite(hours) || hours < 0 || hours > 24) {
      throw new Error(t('admin.payroll.settleRange', { defaultValue: 'Settled hours must be between 0 and 24' }));
    }
    await salaryService.settleDispute(timeEntryId, hours);
    await Promise.all([loadDisputes(), load()]);
    return t('admin.payroll.settled', { defaultValue: 'Dispute settled' });
  });

  const load = useCallback(async ({ selectedIds } = {}) => {
    try {
      setLoading(true); setError(null); setErrorConfigurationUrl(null);
      const res = await salaryService.getOverview(year, month, tempusFilter, danlonFilter, 'all');
      dirty.current = new Set();
      adminDirty.current = new Set();
      allocationDirty.current = new Set();
      const unpaidIds = new Set(res.rows.filter((row) => !isPaid(row)).map((row) => row.employeeId));
      selectionDirty.current = new Map([...selectionDirty.current].filter(([employeeId]) => unpaidIds.has(employeeId)));
      setData(res);
      setRows(res.rows);
      setRefreshRequired(false);
      // Unconfigured/null rows keep automatic selection when payable. Preserve
      // unsaved checkbox edits across secondary refreshes, but never select a
      // paid, forwarded, empty or invalid row solely because its saved flag is true.
      setSelected(new Set(res.rows.filter((r) => hasPayableInputs(r)
        && (selectionDirty.current.get(r.employeeId) ?? r.exportSelected) !== false
        && (!selectedIds || selectedIds.has(r.employeeId))).map((r) => r.employeeId)));
      return res;
    } catch (err) {
      setRefreshRequired(true);
      setError(err.message);
      setErrorConfigurationUrl(settingsLinkFromError(err));
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDisputes(); }, [loadDisputes]);
  const loadDanlonTransfers = useCallback(async () => {
    try {
      const result = await salaryService.getDanlonTransfers(year, month);
      setDanlonTransfers(result.transfers || []); setTransferHistoryError('');
    } catch (err) { setDanlonTransfers([]); setTransferHistoryError(err.message); }
  }, [year, month]);
  useEffect(() => { loadDanlonTransfers(); }, [loadDanlonTransfers]);

  const changeMonth = (y, m) => run(async () => {
    await flushEdits();
    if (y === year && m === month) await load();
    else { setLoading(true); setData(null); setRows([]); }
    setMonth(m); setYear(y); setNotice(null);
    setPaymentView(y * 12 + m < now.getFullYear() * 12 + now.getMonth() + 1 ? 'all' : 'unpaid');
  });
  const shiftMonth = (d) => {
    let m = month + d; let y = year;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    changeMonth(y, m);
  };
  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
  const locked = Boolean(data && data.locked);
  const fxMeta = (data && data.meta) || data || {};
  const fxRate = fxMeta.fxRate ?? data?.fxRate ?? data?.eurDkkRate ?? null;
  const fxSource = fxMeta.fxSource ?? data?.fxSource ?? (data?.eurDkkRate != null ? 'Settings → General' : null);
  const fxSourceLabel = fxSource === 'settings.general.eurDkkRate' ? 'Settings → General' : fxSource;
  const fxSetAt = fxMeta.fxSetAt ?? data?.fxSetAt ?? null;
  const fxFrozenAt = fxMeta.fxFrozenAt ?? data?.fxFrozenAt ?? null;
  const fxStatus = fxMeta.fxStatus ?? data?.fxStatus ?? (locked ? 'frozen' : 'provisional');
  const fxFrozen = fxStatus === 'frozen' || locked;

  const setField = (employeeId, field, value) => {
    setRows((prev) => prev.map((r) => (r.employeeId === employeeId ? { ...r, [field]: value } : r)));
    dirty.current.add(employeeId);
    if (field === 'adminHours') adminDirty.current.add(employeeId);
  };

  const setSalaryItemAllocation = (employeeId, itemId, value) => {
    const [kind, rawId] = String(value || 'unallocated').split(':');
    const placementId = kind === 'placement' ? rawId : null;
    const customerId = kind === 'customer' ? (Number(rawId) || rawId) : null;
    setRows((prev) => prev.map((row) => {
      if (row.employeeId !== employeeId) return row;
      const key = Array.isArray(row.salaryItems) ? 'salaryItems' : 'allocations';
      const items = salaryItemsForRow(row).map((item) => (
        String(item.id || item.type) === String(itemId)
          ? { ...item, placementId, customerId }
          : item
      ));
      return { ...row, [key]: items };
    }));
    dirty.current.add(employeeId);
    allocationDirty.current.add(employeeId);
  };

  const updateSelection = (employeeIds, checked) => {
    employeeIds.forEach((employeeId) => selectionDirty.current.set(employeeId, checked));
    setSelected((prev) => {
      const next = new Set(prev);
      employeeIds.forEach((employeeId) => { if (checked) next.add(employeeId); else next.delete(employeeId); });
      return next;
    });
  };
  const toggleSelect = (employeeId) => updateSelection([employeeId], !selected.has(employeeId));
  const visibleRows = rows.filter((r) => (paymentView === 'all' || !isPaid(r))
    && matchesPayrollSearch(r, search)
    && (taxFilter === 'all' || (taxFilter === 'yes' ? r.taxCardReceived === true : r.taxCardReceived === false))
    && (activeFilter === 'all' || (activeFilter === 'active' ? r.tempusActive === true : r.tempusActive === false)));
  const selectableRows = visibleRows.filter(hasPayableInputs);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.employeeId));
  const toggleSelectAll = () => updateSelection(selectableRows.map((r) => r.employeeId), !allSelected);

  // Save checkbox-only edits without posting unrelated financial values. A
  // failed save keeps the pending choices so Save/navigation can be retried.
  const flushEdits = async () => {
    if (dirty.current.size === 0 && selectionDirty.current.size === 0) return true;
    const selectionChanges = new Map(selectionDirty.current);
    const entries = rows
      .filter((r) => (dirty.current.has(r.employeeId) || selectionChanges.has(r.employeeId)) && !isPaid(r))
      .map((r) => {
        const selection = { employeeId: r.employeeId,
          ...(selectionChanges.has(r.employeeId) ? { exportSelected: selectionChanges.get(r.employeeId) } : {}) };
        if (!dirty.current.has(r.employeeId)) return selection;
        return { ...selection,
          premie: r.premie || 0,
          forudbetaling: r.forudbetaling || 0,
          rejseomk: r.rejseomk || 0,
          feriedage: r.feriedage || 0,
          note: r.note || '',
          // Only send adminHours when the admin actually edited the Admin-Timer
          // field on this row — so editing an unrelated field never pins the
          // auto-tracking default into a frozen override (REQ #22). '' clears the
          // override (back to the default); a number pins it.
          ...(adminDirty.current.has(r.employeeId) ? { adminHours: r.adminHours === '' || r.adminHours == null ? '' : r.adminHours } : {}),
          ...(allocationDirty.current.has(r.employeeId) ? {
            salaryItems: salaryItemsForRow(r).map((item) => ({
              id: item.id,
              type: item.type,
              amountMinor: item.amountMinor,
              currency: item.currency || 'DKK',
              taxable: Boolean(item.taxable),
              placementId: item.placementId || null,
              customerId: item.customerId || null,
            })),
          } : {}),
        };
      });
    if (entries.length) await salaryService.saveEntries(year, month, entries);
    for (const [employeeId, checked] of selectionChanges) {
      if (selectionDirty.current.get(employeeId) === checked) selectionDirty.current.delete(employeeId);
    }
    dirty.current = new Set();
    adminDirty.current = new Set();
    allocationDirty.current = new Set();
    return true;
  };

  // Overfør (REQ #23 / #10 / #11): open a dynamic carry of this employee's
  // payable hours + special items into the next open month. Later-reported hours
  // keep flowing forward; confirmed employee payouts retain their snapshots.
  const handleTransfer = (employeeId) => run(async () => {
    await flushEdits();
    const res = await salaryService.transfer(year, month, employeeId);
    await load();
    return t('admin.payroll.transferred', { defaultValue: 'Carried {{hours}} h forward to {{to}} — later hours follow automatically', hours: res.hours, to: res.to });
  });

  const run = async (fn, successMsg) => {
    try {
      setBusy(true); setError(null); setErrorConfigurationUrl(null); setNotice(null);
      const msg = await fn();
      if (successMsg || msg) setNotice(typeof msg === 'string' ? msg : successMsg);
    } catch (err) {
      setError(err.message);
      setErrorConfigurationUrl(settingsLinkFromError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSave = () => run(async () => {
    await flushEdits();
    // Reload so server-derived fields (payable, Bruttoløn) reflect the edit.
    await load();
    return t('admin.payroll.saved');
  });

  const generateSelectedCsv = (employeeIds, confirmation = {}) => run(async () => {
    const res = await salaryService.generateCsv(year, month, tempusFilter, danlonFilter, employeeIds, confirmation);
    await load();
    if (res.csvEmployees != null && res.noTaxCardEmployees != null) return `Generated Danløn CSV (${res.csvEmployees} employees with tax cards) and Excel (${res.noTaxCardEmployees} employees without tax cards).`;
    return res.skipped
      ? t('admin.payroll.csvGeneratedSkipped', { skipped: res.skipped })
      : t('admin.payroll.csvGenerated');
  });

  const handleGenerateCsv = () => run(async () => {
    await flushEdits();
    const employeeIds = [...selected];
    const preview = await salaryService.previewExport(year, month, tempusFilter, danlonFilter, employeeIds);
    if (!preview.requiresConfirmation) return generateSelectedCsv(employeeIds, { exportPlanHash: preview.exportPlanHash });
    setConfirmAcknowledged(false);
    setConfirm({
      title: t('admin.payroll.earlierPayTypesTitle'),
      text: t('admin.payroll.earlierPayTypesHelp'),
      allocations: preview.allocations,
      ackLabel: t('admin.payroll.earlierPayTypesConfirmed'),
      onYes: () => generateSelectedCsv(employeeIds, { exportPlanHash: preview.exportPlanHash, confirmPayTypes: true }),
    });
  });

  const submitDanlon = (payload) => run(async () => {
    try {
      const result = await salaryService.transferToDanlon(year, month, payload);
      await loadDanlonTransfers();
      return ['uncertain', 'sending'].includes(result.transfer.status)
        ? t('admin.payroll.apiUncertain', { defaultValue: 'Danløn may have received this transfer. Check Danløn and resolve its status in transfer history before trying again.' })
        : t('admin.payroll.apiSubmitted', { defaultValue: 'Submitted to Danløn. Open Løn → Lønberegning → Indlæs fra Integration and import once. Employees have not been marked paid.' });
    } catch (err) {
      await loadDanlonTransfers();
      if (err.code !== 'PAYROLL_PRIOR_CSV') throw err;
      setConfirmAcknowledged(false);
      setConfirm({
        title: t('admin.payroll.apiPriorCsvTitle', { defaultValue: 'A CSV has already been generated' }),
        text: t('admin.payroll.apiPriorCsvHelp', { defaultValue: 'Check Danløn before switching from CSV to API. Sending the same payroll twice can duplicate salary inputs.' }),
        ackLabel: t('admin.payroll.apiPriorCsvAck', { defaultValue: 'I checked that the previous CSV for these employees and this month was not imported, or that those entries were removed in Danløn.' }),
        onYes: () => submitDanlon({ ...payload, acknowledgePriorCsv: true }),
      });
      return null;
    }
  });
  const askDanlonTransfer = () => run(async () => {
    const employeeIds = [...selected];
    await flushEdits();
    const latest = await load({ selectedIds: new Set(employeeIds) });
    if (!latest) return;
    const current = latest.rows.filter((row) => employeeIds.includes(row.employeeId));
    if (current.length !== employeeIds.length || current.some((row) => !hasPayableInputs(row) || row.exportSelected === false)) {
      throw new Error(t('admin.payroll.apiSelectionChanged', { defaultValue: 'The selected payroll rows changed. Review the refreshed month before transferring.' }));
    }
    const expectedRevisions = Object.fromEntries(current.map((row) => [row.employeeId, row.payrollRevision]));
    const signature = JSON.stringify({ year, month, employeeIds: [...employeeIds].sort(), expectedRevisions });
    if (transferAttempt.current?.signature !== signature) transferAttempt.current = { signature, requestId: transferRequestId() };
    const payload = { employeeIds, expectedRevisions, requestId: transferAttempt.current.requestId };
    setConfirmAcknowledged(false);
    setConfirm({
      title: t('admin.payroll.apiTransferTitle', { defaultValue: 'Transfer selected payroll to Danløn' }),
      text: t('admin.payroll.apiTransferHelp', { defaultValue: 'Send {{count}} selected employees for {{month}} using the saved Tempus pay type mappings. This creates salary inputs in Danløn; it does not pay employees or mark them paid. Import the transfer once in Danløn.', count: employeeIds.length, month: monthLabel }),
      onYes: () => submitDanlon(payload),
    });
  });

  const changeTaxCard = (row, taxCardReceived) => run(async () => {
    await flushEdits();
    const latest = await salaryService.getOverview(year, month, 'all', 'all', 'all');
    const current = latest.rows.find((r) => r.employeeId === row.employeeId);
    if (!current || isPaid(current) || Boolean(current.taxCardReceived) !== Boolean(row.taxCardReceived)) {
      throw new Error(t('admin.payroll.taxCardChanged', { defaultValue: 'This employee’s payroll status changed. Refresh the month before updating Tax Card.' }));
    }
    await salaryService.updateTaxCard(row.employeeId, { taxCardReceived, expectedRevision: current.payrollRevision, year, month });
    const refreshed = await load();
    if (!refreshed) throw new Error(t('admin.payroll.taxCardRefreshFailed', { defaultValue: 'Tax Card was saved, but payroll could not be refreshed. Reload the month before continuing.' }));
    return t('admin.payroll.taxCardSaved', { defaultValue: 'Tax Card updated. Review the payment basis before exporting or confirming payment.' });
  });

  const handleDownloadExcel = () => run(async () => {
    await flushEdits();
    await salaryService.downloadExcel(year, month, tempusFilter, danlonFilter);
    await load();
    return t('admin.payroll.excelDownloaded');
  });

  const handleImportExcel = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    run(async () => {
      const report = await salaryService.importExcel(year, month, file);
      await load();
      return t('admin.payroll.excelImported', { updated: report.updated, skipped: report.skipped });
    });
  };

  const askLock = () => {
    setConfirmAcknowledged(false);
    setConfirm({
      title: t('admin.payroll.lockTitle'),
      text: `Freeze the month FX rate. This does not confirm employee payments; unpaid rows remain editable. ${fxRate != null
        ? `The ${fxStatus === 'provisional' ? 'provisional ' : ''}FX rate 1 EUR = ${Number(fxRate).toLocaleString('da-DK', { maximumFractionDigits: 6 })} DKK will be frozen for this payroll month.`
        : 'No FX rate is currently available; verify the rate before continuing.'}`,
      ackLabel: 'I have reviewed the month preparation and FX rate. Freeze this rate; employee payments are confirmed separately.',
      confirmLabel: 'Freeze preparation & FX',
      variant: 'danger',
      onYes: () => run(async () => {
        await flushEdits();
        await salaryService.lockMonth(year, month);
        await load();
        return t('admin.payroll.locked');
      }),
    });
  };

  const askUnlock = () => {
    setConfirmAcknowledged(false);
    setConfirm({
      title: t('admin.payroll.unlockTitle'),
      text: 'Unlocking makes the month FX rate provisional again. Confirmed employee payments and their reports remain preserved.',
      ackLabel: 'I understand that the preparation and FX rate become provisional. Confirmed employee payments stay preserved.',
      confirmLabel: 'Unlock & make provisional',
      variant: 'danger',
      onYes: () => run(async () => {
        await salaryService.unlockMonth(year, month);
        await load();
        return t('admin.payroll.unlocked');
      }),
    });
  };

  const openPayment = (row) => run(async () => {
    await flushEdits();
    const latest = await salaryService.getOverview(year, month, 'all', 'all', 'all');
    setData(latest); setRows(latest.rows);
    setSelected((previous) => new Set(latest.rows.filter((r) => previous.has(r.employeeId) && r.exportSelected !== false && hasPayableInputs(r)).map((r) => r.employeeId)));
    const reviewedRow = latest.rows.find((r) => r.employeeId === row.employeeId);
    if (!reviewedRow || !hasPayoutInputs(reviewedRow)) throw new Error('This payroll row has changed or has no amount to pay. Refresh the month before confirming payment.');
    if (isSalaryAdvance(reviewedRow) && (!Number.isSafeInteger(reviewedRow.taxCardPayroll.payNowDkkMinor) || reviewedRow.taxCardPayroll.payNowDkkMinor <= 0)) {
      throw new Error(t('admin.payroll.advanceUnavailable', { defaultValue: 'The salary advance has no positive payment amount. Check the payroll inputs before confirming payment.' }));
    }
    const date = new Date();
    setPaymentRow(reviewedRow); setPaidDate(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);
    setPaymentReference('');
    setPaymentAmount(isSalaryAdvance(reviewedRow) ? (reviewedRow.taxCardPayroll.payNowDkkMinor / 100).toFixed(2) : '');
    setError(null);
  });
  const confirmPayment = (event) => {
    event.preventDefault();
    run(async () => {
      await salaryService.confirmPayout({ year, month, employeeId: paymentRow.employeeId, paidDate,
        expectedRevision: paymentRow.payrollRevision,
        reference: paymentReference.trim(), ...(paymentAmount !== '' ? { amountDkk: paymentAmount } : {}) });
      setPaymentRow(null);
      await load();
      return 'Payment confirmed. The payroll calculation and reports are preserved.';
    });
  };
  const downloadReport = (payoutId, format) => run(async () => {
    await salaryService.downloadPayoutReport(payoutId, format);
  });

  const totalHours = visibleRows.reduce((s, r) => s + (Number(r.hours) || 0), 0);
  const placementOptionsFor = (row) => {
    const candidates = [
      ...getPlacements(row),
      ...(Array.isArray(row.availablePlacements) ? row.availablePlacements : []),
      ...(Array.isArray(data?.placements)
        ? data.placements.filter((placement) => Number(placement.employeeId) === Number(row.employeeId))
        : []),
    ];
    return [...new Map(candidates.filter((placement) => placement && placement.id)
      .map((placement) => [String(placement.id), placement])).values()];
  };
  const customerOptionsFor = (row, placements) => {
    const explicit = [
      ...(Array.isArray(row.customers) ? row.customers : []),
      ...(Array.isArray(row.assignedCustomers) ? row.assignedCustomers : []),
      ...(Array.isArray(data?.customers) ? data.customers : []),
    ];
    const fromPlacements = placements.map((placement) => ({
      id: placement.customerId,
      name: placementCustomerName(placement, explicit),
    }));
    return [...new Map([...explicit, ...fromPlacements]
      .filter((customer) => customer && customer.id)
      .map((customer) => [String(customer.id), customer])).values()];
  };

  return (
    <Page ref={pageRef} data-payroll-page>
      <StatusSlot aria-live="polite">{error && <Alert $type="error" role="alert">{error}
        {refreshRequired && <button type="button" disabled={working || loading} onClick={() => run(() => load())} style={{ marginLeft: 8 }}>Refresh payroll</button>}
        {errorConfigurationUrl && <div style={{ marginTop: 6 }}><a href={errorConfigurationUrl} target="_blank" rel="noopener noreferrer">
          {t('admin.payroll.openPayrollSettings', { defaultValue: 'Open Tempus Danløn payroll settings' })}
        </a></div>}
      </Alert>}
      {notice && <Alert $type="success">{notice}</Alert>}
      </StatusSlot>

      <Toolbar data-testid="payroll-toolbar">
        <Bar>
          <MonthNav>
            <NavBtn aria-label="Previous month" disabled={busy || loading} onClick={() => shiftMonth(-1)}><FiChevronLeft /></NavBtn>
            <MonthLabel>{monthLabel}</MonthLabel>
            <NavBtn aria-label="Next month" disabled={busy || loading} onClick={() => shiftMonth(1)}><FiChevronRight /></NavBtn>
            <NavBtn disabled={busy || loading} onClick={() => changeMonth(now.getFullYear(), now.getMonth() + 1)}>{t('admin.payroll.thisMonth')}</NavBtn>
          </MonthNav>
          <Group>
            <FilterLabel>
              Payroll view
              <Sel aria-label="Payroll view" value={paymentView} onChange={(e) => setPaymentView(e.target.value)}>
                <option value="unpaid">To be paid / forwarded</option>
                <option value="all">Month history, including paid</option>
              </Sel>
            </FilterLabel>
            <Toggle onClick={() => setShowSpecial((v) => !v)}>
              {showSpecial ? <FiChevronDown /> : <FiChevronRt />} {t('admin.payroll.specialItems')}
            </Toggle>
          </Group>
          {locked
            ? <LockPill $locked><FiLock /> {t('admin.payroll.lockedBadge')}</LockPill>
            : <LockPill><FiUnlock /> {t('admin.payroll.openBadge')}</LockPill>}
        </Bar>
        <Group style={{ marginTop: '.55rem' }}>
          <SearchInput type="search" aria-label="Search payroll" placeholder="Search name, CPR, employee number, customer, tax card, active / inactive…"
            value={search} onChange={(event) => setSearch(event.target.value)} />
          <Sel aria-label="Filter tax card" value={taxFilter} onChange={(event) => setTaxFilter(event.target.value)}>
            <option value="all">All tax cards</option><option value="yes">Tax card received</option><option value="no">No tax card</option>
          </Sel>
          <Sel aria-label="Filter Tempus account status" value={activeFilter} onChange={(event) => setActiveFilter(event.target.value)}>
            <option value="all">All account statuses</option><option value="active">Active in Tempus</option><option value="inactive">Inactive in Tempus</option>
          </Sel>
        </Group>
        <FxBar>
          <FxSummary>
            <strong>EUR/DKK payroll FX</strong>
            <FxStatus $frozen={fxFrozen}>{fxFrozen ? 'Frozen' : 'Provisional'}</FxStatus>
            <span>{fxRate != null ? `1 EUR = ${Number(fxRate).toLocaleString('da-DK', { maximumFractionDigits: 6 })} DKK` : 'Rate unavailable'}</span>
          </FxSummary>
          <FxMeta>
            {fxSourceLabel ? `Source: ${fxSourceLabel}` : 'Source not recorded'}
            {fxSetAt ? ` · set ${fmtTimestamp(fxSetAt)}` : ''}
            {fxFrozenAt ? ` · frozen ${fmtTimestamp(fxFrozenAt)}` : ''}
            {!fxFrozen ? ' · each payment confirmation preserves its calculation rate' : ''}
          </FxMeta>
        </FxBar>
      </Toolbar>

      <DetailsBar>
      <Details><summary>Payroll guidance</summary><div>
        <strong>{monthLabel}: {paymentView === 'all' ? 'Payroll history — paid employees are shown in grey.' : 'To be paid — only unpaid employees in this month’s payroll process are shown.'}</strong>
        <div style={{ marginTop: 5, fontSize: '0.86rem' }}>
          Everyone employed during any part of the month is included, even with zero hours, together with former employees with outstanding payroll inputs.
          {paymentView === 'all' ? ' Paid rows and their saved reports remain read-only.' : ' Confirmed paid rows are hidden in this view.'}
          {' '}Source changes flow to the next unpaid month; confirmed payouts stay fixed. Employment status shown is for the selected month.
        </div>
        <div style={{ marginTop: 5, fontSize: '0.86rem' }}>
          {t('admin.payroll.taxCardHelp', { defaultValue: 'Tax Card received: Løn (Bér). No tax card: estimated Forskud (Bérelöleg), with Forudbetaling and Refund deferred until regular salary. The left checkbox selects CSV export or API transfer; Tax Card does not confirm payment.' })}
        </div>
      </div></Details>
      <Details><summary>{t('admin.payroll.apiHistoryTitle', { defaultValue: 'Danløn transfer history' })}</summary><div>
        <h2 style={{ fontSize: '1rem', marginTop: 0 }}>{t('admin.payroll.apiHistoryTitle', { defaultValue: 'Danløn transfer history' })}</h2>
        <p>{t('admin.payroll.apiHistoryHelp', { defaultValue: 'A successful API transfer still needs to be imported once in Danløn. This history does not confirm salary payment. Submitted or uncertain transfers block duplicate CSV/API exports for the same employees and month.' })}</p>
        {transferHistoryError && <p role="alert">{transferHistoryError}</p>}
        <Btn $variant="ghost" disabled={busy} onClick={loadDanlonTransfers}>{t('admin.payroll.refreshTransfers', { defaultValue: 'Refresh transfers' })}</Btn>
        {danlonTransfers.map((entry) => <div key={entry.id} style={{ borderTop: '1px solid var(--border-subtle)', marginTop: 12, paddingTop: 12, overflowWrap: 'anywhere' }}>
          <strong>{t(`admin.payroll.apiStatuses.${entry.status}`, { defaultValue: entry.status })}</strong>{' · '}{fmtTimestamp(entry.createdAt)}{' · '}{entry.employeeIds?.length || 0} {t('admin.payroll.apiEmployees', { defaultValue: 'employees' })}{' · '}{entry.lineCount} {t('admin.payroll.apiLines', { defaultValue: 'lines' })}
          <div><small>{entry.id}</small></div>
          {entry.employeeIds?.length > 0 && <div>{entry.employeeIds.map((id) => rows.find((row) => String(row.employeeId) === String(id))?.name || `#${id}`).join(', ')}</div>}
          {['uncertain', 'sending', 'succeeded', 'confirmed'].includes(entry.status) && <Actions>
            {['uncertain', 'sending'].includes(entry.status) && <Btn $variant="ghost" disabled={busy} onClick={() => { setResolveTransfer({ id: entry.id, outcome: 'confirmed' }); setResolveReason(''); setResolveAcknowledged(false); }}>{t('admin.payroll.apiConfirmReceived', { defaultValue: 'Confirm found in Danløn' })}</Btn>}
            <Btn $variant="ghost" disabled={busy} onClick={() => { setResolveTransfer({ id: entry.id, outcome: 'cleared' }); setResolveReason(''); setResolveAcknowledged(false); }}>{t('admin.payroll.apiClear', { defaultValue: 'Confirm absent / removed in Danløn' })}</Btn>
          </Actions>}
        </div>)}
      </div></Details>
      </DetailsBar>

      {disputes.length > 0 && (
        <Details><summary>{t('admin.payroll.disputesTitle', { defaultValue: 'Contested days awaiting your decision' })} ({disputes.length})</summary><div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <FiAlertCircle style={{ color: 'var(--act-danger)' }} />
            <strong style={{ color: 'var(--ve-ink-800)' }}>
              {t('admin.payroll.disputesTitle', { defaultValue: 'Contested days awaiting your decision' })} ({disputes.length})
            </strong>
          </div>
          <TableWrap>
            <Table $wide>
              <thead>
                <tr>
                  <Th>{t('admin.payroll.name')}</Th>
                  <Th>{t('common.date')}</Th>
                  <Th>{t('common.customer')}</Th>
                  <Th $num>{t('admin.payroll.disputeLogged', { defaultValue: 'Employee' })}</Th>
                  <Th $num>{t('admin.payroll.disputeSuggested', { defaultValue: 'Customer' })}</Th>
                  <Th>{t('admin.payroll.disputeCustomerNote', { defaultValue: 'Customer note' })}</Th>
                  <Th>{t('admin.payroll.disputeNote', { defaultValue: 'Employee note' })}</Th>
                  <Th $num>{t('admin.payroll.disputeSettle', { defaultValue: 'Settle (h)' })}</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {disputes.map((d) => (
                  <tr key={d.timeEntryId}>
                    <Td>{d.employeeName}</Td>
                    <Td>{d.date}</Td>
                    <Td>{d.customerName || '—'}</Td>
                    <Td $num>{fmt(d.loggedHours)}</Td>
                    <Td $num style={{ color: 'var(--act-danger)' }}>{d.suggestedHours != null ? fmt(d.suggestedHours) : '—'}</Td>
                    <Td style={{ whiteSpace: 'normal', maxWidth: 240 }}>{d.challengeReason || '—'}</Td>
                    <Td style={{ whiteSpace: 'normal', maxWidth: 240 }}>{d.employeeResponse || '—'}</Td>
                    <Td $num>
                      <NumInput
                        type="number" step="0.25" min="0" max="24"
                        value={settleInputs[d.timeEntryId] ?? ''}
                        onChange={(e) => setSettleInputs((prev) => ({ ...prev, [d.timeEntryId]: e.target.value }))}
                      />
                    </Td>
                    <Td>
                      <Btn onClick={() => settleDispute(d.timeEntryId)} disabled={busy}>
                        {t('admin.payroll.settle', { defaultValue: 'Settle' })}
                      </Btn>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </div></Details>
      )}

      <PayrollCard aria-busy={loading || busy}>
        {loading && !data ? (
          <Empty>{t('common.loading')}</Empty>
        ) : visibleRows.length === 0 ? (
          <Empty>{t('admin.payroll.noData')}</Empty>
        ) : (
          <>
            <TableWrap tabIndex={0} role="region" aria-label="Payroll employees; scroll horizontally for all columns">
              <Table>
                <colgroup>{[48, 88, 120, 220, 95, 260, 210, 115, 115, 100, 155, 165, 125, 90, 115, 160, 64].map((width, index) => <col key={index} style={{ width }} />)}</colgroup>
                <thead>
                  <tr>
                    <Th style={{ textAlign: 'center' }} title={t('admin.payroll.includeInCsv')}>
                      <input type="checkbox" checked={allSelected} disabled={busy} onChange={toggleSelectAll} title={t('admin.payroll.selectAll')} />
                    </Th>
                    <Th>{t('admin.payroll.medarbNr')}</Th>
                    <Th>{t('admin.payroll.cpr')}</Th>
                    <Th>{t('admin.payroll.name')}</Th>
                    <Th>{t('admin.payroll.taxCard', { defaultValue: 'Tax Card' })}</Th>
                    <Th>{t('admin.payroll.paymentBasis', { defaultValue: 'Payment basis' })}<Help text={payrollHelp.paymentBasis} /></Th>
                    <Th>Employment in month</Th>
                    <Th>{t('admin.payroll.tempusActive')}</Th>
                    <Th>{t('admin.payroll.danlonActive')}</Th>
                    <Th $num>{t('admin.payroll.approved')}<Help text={payrollHelp.approved} /></Th>
                    <Th $num>{t('admin.payroll.employeeHours', { defaultValue: 'Medarbejder-Timer' })}<Help text={payrollHelp.employeeHours} /></Th>
                    <Th $num>{t('admin.payroll.adminHours', { defaultValue: 'Admin-Timer' })}<Help text={payrollHelp.adminHours} /></Th>
                    <Th $num title={t('admin.payroll.bruttoTitle', { defaultValue: 'Source hours × their recorded EUR rates and payroll EUR/DKK. Includes gross earnings from advances being settled; Forskud uses the separate estimate.' })}>
                      {t('admin.payroll.brutto', { defaultValue: 'Bruttoløn' })}
                      <Help text={payrollHelp.gross} />
                    </Th>
                    <Th>Vacation<Help text="The employee’s vacation declaration for the displayed month. This does not set paid holiday days or change salary." /></Th>
                    {/* Præmie / Forudbetaling / Refund. / Afholdte feriedage /
                        Noter deliberately have NO columns here — they are edited
                        in the stacked panel under each row. */}
                    <Th style={{ textAlign: 'center' }}>{t('admin.payroll.transfer', { defaultValue: 'Overfør' })}</Th>
                    <Th style={{ textAlign: 'center' }}>Payment / reports</Th>
                    <Th style={{ textAlign: 'center' }}>Done<Help text={payrollHelp.done} /></Th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => {
                    const paid = isPaid(r);
                    const readOnly = paid || r.editable === false;
                    const salaryItems = salaryItemsForRow(r);
                    const placements = placementOptionsFor(r);
                    const customers = customerOptionsFor(r, placements);
                    const unallocatedMinor = unallocatedAmountMinor(r);
                    return (
                    <React.Fragment key={r.employeeId}>
                    <PayrollRow $paid={paid} $expanded={showSpecial} data-testid={`payroll-row-${r.employeeId}`}>
                      <Td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selected.has(r.employeeId)}
                          disabled={busy || !hasPayableInputs(r)}
                          aria-label={`Include ${r.name} in CSV or API transfer`}
                          onChange={() => toggleSelect(r.employeeId)}
                          title={t('admin.payroll.includeInCsv')}
                        />
                      </Td>
                      <Td>{r.medarbNr || '—'}</Td>
                      <Td>{r.cpr || '—'}</Td>
                      <Td>
                        {r.name}
                        <Reason>{payrollReason(r)}</Reason>
                        {unallocatedMinor !== 0 && (
                          <UnallocatedPill title="Special salary items without a placement or customer allocation">
                            Salary item: no customer / placement
                          </UnallocatedPill>
                        )}
                        {Number(r.unallocatedPayableAdminMinutes) > 0 && (
                          <UnallocatedPill title="Payable Admin-Timer that could not be allocated to a placement">
                            Hours: no placement assigned
                          </UnallocatedPill>
                        )}
                      </Td>
                      <Td style={{ textAlign: 'center' }}>
                        {paid && r.taxCardReceived == null ? <>
                          <span title={t('admin.payroll.taxCardNotRecorded', { defaultValue: 'Tax-card status was not recorded for this historical payout' })}>—</span>
                          <Reason>{t('admin.payroll.noTaxCardRecord', { defaultValue: 'Not recorded' })}</Reason>
                        </> : <><input type="checkbox" checked={Boolean(r.taxCardReceived)} disabled={busy || readOnly}
                          aria-label={`${r.name} ${t('admin.payroll.taxCard', { defaultValue: 'Tax Card' })}`}
                          title={paid
                            ? t('admin.payroll.taxCardHistorical', { defaultValue: 'Tax Card status recorded for this payout' })
                            : t('admin.payroll.taxCardReceived', { defaultValue: 'Tax Card received for this employee' })}
                          onChange={(event) => changeTaxCard(r, event.target.checked)} />
                        {paid && <Reason>{t('admin.payroll.atPayout', { defaultValue: 'At payout' })}</Reason>}</>}
                      </Td>
                      <Td style={{ whiteSpace: 'normal', minWidth: 210 }}>
                        <TaxCardSummary row={r} t={t} unsaved={dirty.current.has(r.employeeId)} />
                      </Td>
                      <Td style={{ whiteSpace: 'normal', minWidth: 185 }}>
                        <strong>{r.employedInMonth ? 'Employed during this month' : 'Not employed during this month'}</strong>
                        {(Array.isArray(r.monthEmployments) ? r.monthEmployments : placements).filter((placement) => {
                          const start = `${year}-${String(month).padStart(2, '0')}-01`;
                          const end = `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;
                          return placement.startDate && placement.startDate <= end && (!placement.endDate || placement.endDate >= start);
                        }).map((placement) => <Reason key={placement.id}>
                          {placementCustomerName(placement, customers)}: {formatPlacementDate(placement.startDate)} – {formatPlacementDate(placement.endDate)}
                        </Reason>)}
                      </Td>
                      <Td><Badge $on={r.tempusActive}>{r.tempusActive ? t('admin.payroll.yes') : t('admin.payroll.no')}</Badge></Td>
                      <Td><Badge $on={r.danlonActive === null ? null : r.danlonActive}>{r.danlonActive === null ? '—' : r.danlonActive ? t('admin.payroll.yes') : t('admin.payroll.no')}</Badge></Td>
                      <Td $num>{fmt(r.approvedHours)}</Td>
                      <Td $num>{fmt(r.hours)}</Td>
                      <Td $num title={r.transferredOut ? t('admin.payroll.transferredOut', { defaultValue: 'Transferred to next month' }) : (r.transferredInHours ? t('admin.payroll.transferredIn', { defaultValue: 'Includes {{h}} h carried over', h: fmt(r.transferredInHours) }) : undefined)}>
                        <AmountInput aria-label={`${r.name} Admin hours`}
                          allowNegative={false} disabled={busy || readOnly}
                          style={r.adminOverride ? { borderColor: 'var(--act-primary)', fontWeight: 600 } : undefined}
                          value={r.adminHours ?? ''}
                          onValueChange={(value) => setField(r.employeeId, 'adminHours', value)}
                        />
                        {r.transferredInHours > 0 && <CarriedIn>+{fmt(r.transferredInHours)} h carried in</CarriedIn>}
                        {r.transferredOut && <Reason>Payable here: {fmt(r.payableHours)} h · forwarded to {forwardDestination(r)}</Reason>}
                        {Number(r.unallocatedPayableAdminMinutes) > 0 && (
                          <div style={{ fontSize: '0.7rem', color: '#9a6a10', marginTop: '2px' }}>
                            No placement assigned: {fmt(Number(r.unallocatedPayableAdminMinutes) / 60)} h
                          </div>
                        )}
                      </Td>
                      <Td $num title={r.bruttoDkk != null
                        ? t('admin.payroll.bruttoTitle', { defaultValue: 'Source hours × their recorded EUR rates and payroll EUR/DKK. Includes gross earnings from advances being settled; Forskud uses the separate estimate.' })
                        : t('admin.payroll.bruttoMissing', { defaultValue: 'Sæt medarbejderens Timeløn (EUR) under Employees og EUR/DKK-kursen under Settings → General' })}>
                        {r.bruttoDkk != null ? (
                          r.transferredOut ? (
                            <span style={{ color: 'var(--act-danger)', fontWeight: 600 }}>{fmtDkk(r.bruttoDkk)}</span>
                          ) : (
                            <>
                              {fmtDkk(r.bruttoDkk)}
                              {!!r.carriedInBruttoDkk && (
                                <div style={{ fontSize: '0.7rem', color: 'var(--act-primary)' }}>{r.carriedInBruttoDkk > 0 ? '+' : ''}{fmtDkk(r.carriedInBruttoDkk)}</div>
                              )}
                            </>
                          )
                        ) : '—'}
                      </Td>
                      <Td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={Boolean(r.vacationTaken)} disabled aria-label={`${r.name} Vacation this month`}
                          title={r.vacationDeclared ? (r.vacationTaken ? 'Employee reported vacation this month' : 'Employee reported no vacation this month') : 'No vacation declaration yet for this month'} />
                        {!r.vacationDeclared && <Reason>Not reported</Reason>}
                      </Td>
                      <Td style={{ textAlign: 'center' }}>
                        <XferBtn
                          $done={r.transferredOut}
                          disabled={busy || readOnly || r.transferredOut}
                          title={r.transferredOut ? t('admin.payroll.transferredOut', { defaultValue: 'Transferred to next month' }) : t('admin.payroll.transferHint', { defaultValue: 'Carry these hours forward — later-reported hours flow on automatically' })}
                          onClick={() => handleTransfer(r.employeeId)}
                        >
                          {r.transferredOut
                            ? t('admin.payroll.transferredBtn', { defaultValue: 'Overført' })
                            : t('admin.payroll.transfer', { defaultValue: 'Overfør' })}
                        </XferBtn>
                        <Help text={payrollHelp.transfer} />
                      </Td>
                      <Td style={{ textAlign: 'center', whiteSpace: 'normal', minWidth: 200 }}>
                        {paid ? <>
                          <strong>Paid · {r.payout?.paidDate || ''}</strong>
                          {r.payout?.reference && <Reason>{r.payout.reference}</Reason>}
                          {r.payout?.id && <Group style={{ justifyContent: 'center', marginTop: 6 }}>
                            <Toggle disabled={busy} onClick={() => downloadReport(r.payout.id, 'pdf')}>PDF report</Toggle>
                            <Toggle disabled={busy} onClick={() => downloadReport(r.payout.id, 'xlsx')}>Excel report</Toggle>
                          </Group>}
                        </> : <><Btn disabled={busy || readOnly || !hasPayoutInputs(r)} onClick={() => openPayment(r)}>Mark paid</Btn><Help text={payrollHelp.paid} /></>}
                      </Td>
                      <Td style={{ textAlign: 'center' }}><input type="checkbox" checked={done.has(r.employeeId)} onChange={() => toggleDone(r.employeeId)} aria-label={`${r.name} Done`} title={payrollHelp.done} /></Td>
                    </PayrollRow>
                    {/* One panel per employee whenever the special items are shown —
                        not only when normalized allocation rows exist, because the
                        editable fields now live here and must be reachable for
                        somebody who has not entered anything yet. */}
                    {showSpecial && (
                      <SpecialRow data-testid={`payroll-special-${r.employeeId}`}>
                        <Td colSpan={17} style={{ padding: 0, whiteSpace: 'normal' }}>
                          <ItemPanel>
                            <ItemTitle>{r.name} · {t('admin.payroll.specialItems')}</ItemTitle>
                            <ItemFields>
                              {/* Præmie is a RATE per hour; the derived line under it
                                  shows the amount the CSV will actually carry so the
                                  operator never has to do the multiplication. */}
                              <Field title={t('admin.payroll.premieTitle')}>
                                <span>{t('admin.payroll.premie')}<Help text={payrollHelp.premium} /></span>
                                <FieldRow>
                                  <AmountInput aria-label={`${r.name} Premium`} disabled={busy || readOnly} value={r.premie ?? ''} onValueChange={(value) => setField(r.employeeId, 'premie', value)} />
                                  <Unit>{t('admin.payroll.premiePerHour')}</Unit>
                                </FieldRow>
                                <Derived>
                                  {r.premie ? t('admin.payroll.premieAmount', { amount: `${fmt(r.premieAmountDkk)} DKK` }) : ''}
                                </Derived>
                                {/* Carried-in items are SIGNED: Forudbetaling/Refund.
                                    deductions arrive negative, so the line shows on any
                                    non-zero carry, not only a positive one. */}
                                {!!r.carriedInPremie && <CarriedIn>{r.carriedInPremie > 0 ? '+' : ''}{fmt(r.carriedInPremie)}</CarriedIn>}
                              </Field>
                              <Field>
                                <span>{t('admin.payroll.forudbetaling')}<Help text={payrollHelp.advance} /></span>
                                <FieldRow>
                                  <AmountInput aria-label={`${r.name} Forudbetaling`} disabled={busy || readOnly} value={r.forudbetaling ?? ''} onValueChange={(value) => setField(r.employeeId, 'forudbetaling', value)} />
                                  <Unit>DKK</Unit>
                                </FieldRow>
                                {!!r.carriedInForudbetaling && <CarriedIn>{r.carriedInForudbetaling > 0 ? '+' : ''}{fmt(r.carriedInForudbetaling)}</CarriedIn>}
                              </Field>
                              <Field>
                                <span>{t('admin.payroll.rejseomk')}<Help text={payrollHelp.refund} /></span>
                                <FieldRow>
                                  <AmountInput aria-label={`${r.name} Refund`} disabled={busy || readOnly} value={r.rejseomk ?? ''} onValueChange={(value) => setField(r.employeeId, 'rejseomk', value)} />
                                  <Unit>DKK</Unit>
                                </FieldRow>
                                {!!r.carriedInRejseomk && <CarriedIn>{r.carriedInRejseomk > 0 ? '+' : ''}{fmt(r.carriedInRejseomk)}</CarriedIn>}
                              </Field>
                              {/* Days, not money: step 0.5 so half days are natural and
                                  min 0 because a negative holiday count is meaningless. */}
                              <Field title={t('admin.payroll.feriedageTitle')}>
                                <span>{t('admin.payroll.feriedage')}<Help text={payrollHelp.holiday} /></span>
                                <FieldRow>
                                  <AmountInput aria-label={`${r.name} Holiday days`} allowNegative={false} disabled={busy || readOnly} value={r.feriedage ?? ''} onValueChange={(value) => setField(r.employeeId, 'feriedage', value)} />
                                  <Unit>{t('admin.payroll.feriedageUnit')}</Unit>
                                </FieldRow>
                              </Field>
                              <Field>
                                {t('admin.payroll.note', { defaultValue: 'Noter' })}
                                <NoteInput type="text" disabled={busy || readOnly} value={r.note ?? ''} onChange={(e) => setField(r.employeeId, 'note', e.target.value)} />
                              </Field>
                            </ItemFields>
                            {salaryItems.length > 0 && (
                            <ItemTitle>
                              Assign salary items to a customer / placement<Help text={payrollHelp.allocation} />
                              {unallocatedMinor !== 0 && (
                                <UnallocatedPill>
                                  No customer / placement: {fmt(unallocatedMinor / 100)} {salaryItems[0]?.currency || 'DKK'}
                                </UnallocatedPill>
                              )}
                            </ItemTitle>
                            )}
                            {salaryItems.map((item, index) => {
                              const allocationValue = item.placementId
                                ? `placement:${item.placementId}`
                                : item.customerId
                                  ? `customer:${item.customerId}`
                                  : 'unallocated';
                              return (
                                <ItemRow key={item.id || `${item.type}-${index}`}>
                                  <strong>{salaryTypeLabel(item.type)}<Help text={payrollHelp[item.type === 'premium' ? 'premiumTotal' : item.type === 'advance' ? 'advance' : 'refund']} /></strong>
                                  <span>{fmtItemAmount(item)}</span>
                                  <span>{item.taxable ? 'Taxable' : 'Not taxable'}</span>
                                  <AllocationSelect
                                    aria-label={`${r.name} ${salaryTypeLabel(item.type)} allocation`}
                                    value={allocationValue}
                                    $unallocated={allocationValue === 'unallocated'}
                                    disabled={busy || readOnly}
                                    onChange={(event) => setSalaryItemAllocation(r.employeeId, item.id || item.type, event.target.value)}
                                  >
                                    <option value="unallocated">No customer / placement assigned</option>
                                    {placements.map((placement) => (
                                      <option key={`placement-${placement.id}`} value={`placement:${placement.id}`}>
                                        Placement: {placementCustomerName(placement, customers)} · {formatPlacementDate(placement.startDate)} – {formatPlacementDate(placement.endDate)}
                                      </option>
                                    ))}
                                    {customers.map((customer) => (
                                      <option key={`customer-${customer.id}`} value={`customer:${customer.id}`}>
                                        Customer only: {customer.name || customer.companyName || `#${customer.id}`}
                                      </option>
                                    ))}
                                  </AllocationSelect>
                                </ItemRow>
                              );
                            })}
                          </ItemPanel>
                        </Td>
                      </SpecialRow>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>
          </>
        )}
          <Footer data-testid="payroll-footer">
            <Total><span style={{ fontSize: '.75rem', fontWeight: 400 }} title="Filters only change the rows shown. The selected count includes hidden rows.">{visibleRows.length}/{rows.length} shown · {selected.size} selected</span><span>{t('admin.payroll.totalHours')}: {fmt(totalHours)}</span></Total>

            <Actions style={{ marginTop: '.35rem', gap: '.4rem' }}>
              {rows.some((row) => !isPaid(row)) && (
                <ActionHelp><Btn onClick={handleSave} disabled={busy || loading} title={payrollHelp.save}><FiSave /> {t('admin.payroll.save')}</Btn><Help text={payrollHelp.save} /></ActionHelp>
              )}
              <ActionHelp><Btn
                $variant="download"
                onClick={handleGenerateCsv}
                disabled={busy || loading || selected.size === 0}
                title={selected.size === 0 ? t('admin.payroll.selectNone') : payrollHelp.csv}
              >
                <FiFileText /> {t('admin.payroll.generateCsv')}{selected.size ? ` (${selected.size})` : ''}
              </Btn><Help text={payrollHelp.csv} /></ActionHelp>
              <ActionHelp><Btn $variant="ghost" disabled={busy || loading || selected.size === 0} onClick={askDanlonTransfer} title={payrollHelp.api}>
                <FiUploadCloud /> {t('admin.payroll.transferToDanlon', { defaultValue: 'Transfer to Danløn API' })}{selected.size ? ` (${selected.size})` : ''}
              </Btn><Help text={payrollHelp.api} /></ActionHelp>
              <ActionHelp><Btn $variant="ghost" onClick={handleDownloadExcel} disabled={busy || loading} title={payrollHelp.download}>
                <FiDownload /> {t('admin.payroll.downloadExcel')}
              </Btn><Help text={payrollHelp.download} /></ActionHelp>
              <input ref={importRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleImportExcel} />
              <ActionHelp><Btn $variant="ghost" onClick={() => importRef.current && importRef.current.click()} disabled={busy || loading} title={payrollHelp.import}>
                <FiUploadCloud /> {t('admin.payroll.importExcel')}
              </Btn><Help text={payrollHelp.import} /></ActionHelp>
              <ActionHelp>{locked
                ? <Btn $variant="danger" onClick={askUnlock} disabled={busy || loading} title={payrollHelp.unlock}><FiUnlock /> {t('admin.payroll.unlockMonth')}</Btn>
                : <Btn $variant="danger" onClick={askLock} disabled={busy || loading} title={payrollHelp.freeze}><FiLock /> Freeze preparation &amp; FX</Btn>}<Help text={locked ? payrollHelp.unlock : payrollHelp.freeze} /></ActionHelp>
              <ActionHelp><Btn $variant="ghost" onClick={() => setDoneByMonth((prev) => ({ ...prev, [doneKey]: new Set() }))} title={payrollHelp.done}>Clear checkbox Done</Btn><Help text={payrollHelp.done} /></ActionHelp>
            </Actions>
          </Footer>
      </PayrollCard>



      {resolveTransfer && <Overlay onClick={() => { if (!busy) setResolveTransfer(null); }}>
        <Dialog role="dialog" aria-modal="true" aria-label={t('admin.payroll.apiResolveTitle', { defaultValue: 'Resolve Danløn transfer' })} onClick={(event) => event.stopPropagation()}>
          <form onSubmit={(event) => { event.preventDefault(); run(async () => {
            await salaryService.resolveDanlonTransfer(resolveTransfer.id, resolveTransfer.outcome, resolveReason.trim());
            setResolveTransfer(null); transferAttempt.current = null; await loadDanlonTransfers();
            return t('admin.payroll.apiResolved', { defaultValue: 'Transfer status recorded. Employee payment status is unchanged.' });
          }); }}>
            <DlgTitle>{t('admin.payroll.apiResolveTitle', { defaultValue: 'Resolve Danløn transfer' })}</DlgTitle>
            <DlgText>{resolveTransfer.outcome === 'cleared'
              ? t('admin.payroll.apiClearHelp', { defaultValue: 'Check both pending imports and salary entries in Danløn. Clear this transfer only if its entries are absent or have all been removed. Clearing allows another CSV/API export and can duplicate salary inputs if entries still exist.' })
              : t('admin.payroll.apiConfirmHelp', { defaultValue: 'Confirm only after finding this transfer in Danløn. This keeps duplicate exports blocked; it does not mark employees paid.' })}</DlgText>
            <Field>{t('admin.payroll.apiResolveReason', { defaultValue: 'What you checked in Danløn' })}<NoteInput required minLength={10} maxLength={2000} value={resolveReason} onChange={(event) => setResolveReason(event.target.value)} /></Field>
            <Ack><input type="checkbox" checked={resolveAcknowledged} onChange={(event) => setResolveAcknowledged(event.target.checked)} />{t('admin.payroll.apiResolveAck', { defaultValue: 'I verified the result in Danløn for every employee in this transfer.' })}</Ack>
            <Actions><Btn type="button" $variant="ghost" disabled={busy} onClick={() => setResolveTransfer(null)}>{t('common.cancel')}</Btn><Btn type="submit" disabled={busy || !resolveAcknowledged || resolveReason.trim().length < 10}>{t('common.confirm')}</Btn></Actions>
          </form>
        </Dialog>
      </Overlay>}

      {paymentRow && (
        <Overlay onClick={() => { if (!busy) setPaymentRow(null); }}>
          <Dialog role="dialog" aria-modal="true" aria-label="Confirm salary payment" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={confirmPayment}>
              <DlgTitle>Confirm payment · {paymentRow.name}</DlgTitle>
              <DlgText>{monthLabel}. {t('admin.payroll.paymentEvidenceHelp', { defaultValue: 'Confirm only after payment is completed. This preserves the calculation and makes this employee’s month read-only. Reports explain Tempus inputs and advance estimates; Danløn determines the final tax and net salary.' })}</DlgText>
              {paymentRow.taxCardPayroll ? <div style={{ marginBottom: 16 }}>
                <TaxCardSummary row={paymentRow} t={t} />
                {isSalaryAdvance(paymentRow) && <Reason>{t('admin.payroll.advancePaymentHelp', { defaultValue: 'Includes the estimated net Præmie component. Forudbetaling and Refund remain deferred. Confirm only if the amount below was paid; it will be deducted once when regular salary is settled.' })}</Reason>}
              </div> : <p>Payable hours: <strong>{fmt(paymentRow.payableHours)}</strong><br />
                Premium: {fmt(paymentRow.premieAmountDkk)} DKK<br />
                Advance: {fmt(Number(paymentRow.payableForudbetalingOre || 0) / 100)} DKK<br />
                Refund: {fmt(Number(paymentRow.payableRejseomkOre || 0) / 100)} DKK
              </p>}
              <Field>Payment date<NoteInput required type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} /></Field>
              <Field style={{ marginTop: 12 }}>Payment reference (optional)<NoteInput maxLength={255} value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} /></Field>
              <Field style={{ marginTop: 12 }}>{isSalaryAdvance(paymentRow)
                ? t('admin.payroll.advancePaidAmount', { defaultValue: 'Forskud paid in DKK' }) : 'Amount paid in DKK (optional)'}
                <NumInput type="number" min="0" step="0.01" required={isSalaryAdvance(paymentRow)} readOnly={isSalaryAdvance(paymentRow)} value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} />
              </Field>
              {error && <p role="alert" style={{ color: 'var(--act-danger)' }}>{error}</p>}
              <Actions>
                <Btn type="button" $variant="ghost" disabled={busy} onClick={() => setPaymentRow(null)}>Cancel</Btn>
                <Btn type="submit" disabled={busy}>Confirm paid</Btn>
              </Actions>
            </form>
          </Dialog>
        </Overlay>
      )}

      {confirm && (
        <Overlay onClick={() => { setConfirm(null); setConfirmAcknowledged(false); }}>
          <Dialog role="dialog" aria-modal="true" aria-label={confirm.title} onClick={(e) => e.stopPropagation()}>
            <DlgTitle>{confirm.title}</DlgTitle>
            <DlgText>{confirm.text}</DlgText>
            {confirm.allocations && <AllocationList>{confirm.allocations.map((allocation) => <li key={`${allocation.item}:${allocation.sourceMonth}`}>
              <strong>{t(`admin.integrations.payrollSetup.items.${allocation.item}`)} · {allocation.sourceMonth}</strong>
              <span>{allocation.payTypeName} → {allocation.payslipLabel}</span>
            </li>)}</AllocationList>}
            {confirm.ackLabel && (
              <Ack>
                <input
                  type="checkbox"
                  checked={confirmAcknowledged}
                  onChange={(event) => setConfirmAcknowledged(event.target.checked)}
                />
                <span>{confirm.ackLabel}</span>
              </Ack>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem' }}>
              <Btn $variant="ghost" onClick={() => { setConfirm(null); setConfirmAcknowledged(false); }}>{t('common.cancel')}</Btn>
              <Btn
                $variant={confirm.variant}
                disabled={Boolean(confirm.ackLabel) && !confirmAcknowledged}
                onClick={() => { const fn = confirm.onYes; setConfirm(null); setConfirmAcknowledged(false); fn(); }}
              >
                {confirm.confirmLabel || t('common.confirm')}
              </Btn>
            </div>
          </Dialog>
        </Overlay>
      )}
    </Page>
  );
};

export default Payroll;
